/**
 * 材料记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { MaterialUsageService } from '../../../services/materialUsageService';
import { formatMoney } from '../../../utils/budgetUtils';

interface MaterialPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface MaterialRecord {
  _id: string;
  usageDate: string;
  materialName: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
  totalCost?: number;
  remark?: string;
}

const MaterialPanel: React.FC<MaterialPanelProps> = ({
  projectId,
  isMobile = false,
  active = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MaterialRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0
  });
  const [loaded, setLoaded] = useState(false);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const response = await MaterialUsageService.getMaterialUsages({
        projectId,
        page,
        limit: pageSize
      });
      
      if (response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0
        });
      }
    } catch (error) {
      console.error('获取材料记录失败:', error);
      message.error('获取材料记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  // 首次激活时加载数据
  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const columns: ColumnsType<MaterialRecord> = isMobile ? [
    {
      title: '日期/材料',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{formatDate(record.usageDate)}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {record.materialName} · {record.quantity}{record.unit}
          </div>
          {record.remark && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {record.remark}
            </div>
          )}
        </div>
      )
    },
    {
      title: '金额',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 80,
      align: 'right',
      render: (val: number) => val ? formatMoney(val, false) : '-'
    }
  ] : [
    {
      title: '日期',
      dataIndex: 'usageDate',
      key: 'usageDate',
      width: 100,
      render: (val: string) => formatDate(val)
    },
    {
      title: '材料名称',
      dataIndex: 'materialName',
      key: 'materialName',
      width: 150,
      ellipsis: true
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right'
    },
    {
      title: '单位',
      dataIndex: 'unit',
      key: 'unit',
      width: 60
    },
    {
      title: '单价',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 100,
      align: 'right',
      render: (val: number) => val ? formatMoney(val, false) : '-'
    },
    {
      title: '金额',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 100,
      align: 'right',
      render: (val: number) => val ? formatMoney(val, false) : '-'
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      render: (val: string) => val || '-'
    }
  ];

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  if (!data.length) {
    return <Empty description="暂无材料记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="_id"
      loading={loading}
      size="small"
      pagination={{
        ...pagination,
        size: 'small',
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`,
        onChange: (page, pageSize) => fetchData(page, pageSize)
      }}
    />
  );
};

export default MaterialPanel;
